import React, { useState, useEffect, useCallback } from 'react';
import { knowledgeAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiBookOpen, FiCopy, FiCheck } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function KnowledgePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {} new, {id,...} edit
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (activeCat) params.category = activeCat;
      const [a, c] = await Promise.all([
        knowledgeAPI.list(params),
        knowledgeAPI.categories().catch(() => ({ data: { categories: [] } })),
      ]);
      const list = a.data.articles || [];
      setArticles(list);
      setCategories(c.data.categories || []);
      setSelected(prev => {
        if (prev && list.find(x => x.id === prev.id)) return list.find(x => x.id === prev.id);
        return list[0] || null;
      });
    } catch { toast.error('Xato'); }
    finally { setLoading(false); }
  }, [search, activeCat]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0); // debounce search
    return () => clearTimeout(t);
  }, [load, search]);

  const remove = async (a) => {
    if (!window.confirm(`«${a.title}» o'chirilsinmi?`)) return;
    try { await knowledgeAPI.delete(a.id); toast.success("O'chirildi"); if (selected?.id === a.id) setSelected(null); load(); }
    catch { toast.error('Xato'); }
  };

  const copyBody = () => {
    if (!selected) return;
    navigator.clipboard?.writeText(selected.body || '').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bilim bazasi</h1>
          <p className="page-subtitle">Suhbat skriptlari va tez-tez so'raladigan savollar</p>
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={() => setEdit({})}><FiPlus size={16} /> Yangi maqola</button>}
      </div>

      {/* Search + category chips */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" placeholder="Skript yoki savol qidirish..." style={{ paddingLeft: 38 }}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveCat('')}
            className={`btn btn-sm ${activeCat === '' ? 'btn-primary' : 'btn-ghost'}`}>Barchasi</button>
          {categories.map(c => (
            <button key={c.category} onClick={() => setActiveCat(c.category)}
              className={`btn btn-sm ${activeCat === c.category ? 'btn-primary' : 'btn-ghost'}`}>
              {c.category} <span style={{ opacity: 0.6 }}>({c.count})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: 16, alignItems: 'start' }} className="kb-grid">
          {/* List */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {articles.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Hech narsa topilmadi</div>
            ) : articles.map(a => (
              <button key={a.id} onClick={() => setSelected(a)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                  padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'inherit',
                  background: selected?.id === a.id ? 'var(--accent-light)' : 'transparent',
                  color: selected?.id === a.id ? 'var(--accent)' : 'var(--text-primary)',
                  opacity: a.is_active === false ? 0.5 : 1,
                }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3 }}>
                  {a.category && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.category}</span>}
                  {a.is_active === false && <span className="badge badge-neutral" style={{ fontSize: 10 }}>Nofaol</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Viewer */}
          <div className="card" style={{ minHeight: 240 }}>
            {!selected ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                <FiBookOpen size={32} style={{ opacity: 0.4 }} />
                <p style={{ marginTop: 12 }}>Maqolani tanlang</p>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{selected.title}</h2>
                    {selected.category && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{selected.category}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-icon" onClick={copyBody} title="Matnni nusxalash">
                      {copied ? <FiCheck size={15} color="#10b981" /> : <FiCopy size={15} />}
                    </button>
                    {isAdmin && <>
                      <button className="btn btn-ghost btn-icon" onClick={() => setEdit(selected)} title="Tahrirlash"><FiEdit2 size={15} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => remove(selected)} title="O'chirish" style={{ color: '#ef4444' }}><FiTrash2 size={15} /></button>
                    </>}
                  </div>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 14, color: 'var(--text-primary)' }}>
                  {selected.body || <span style={{ color: 'var(--text-muted)' }}>Matn yo'q</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {edit && <ArticleForm article={edit} categories={categories}
        onClose={() => setEdit(null)}
        onSaved={(saved) => { setEdit(null); setSelected(saved || null); load(); }} />}
    </div>
  );
}

function ArticleForm({ article, categories, onClose, onSaved }) {
  const isNew = !article.id;
  const [form, setForm] = useState({
    title: article.title || '',
    body: article.body || '',
    category: article.category || '',
    sort_order: article.sort_order || 0,
    is_active: article.is_active !== undefined ? article.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Sarlavha majburiy'); return; }
    setSaving(true);
    try {
      const res = isNew ? await knowledgeAPI.create(form) : await knowledgeAPI.update(article.id, form);
      toast.success('Saqlandi');
      onSaved(res.data.article);
    } catch (e) { toast.error(e.response?.data?.error || 'Xato'); }
    finally { setSaving(false); }
  };

  return (
    <div style={ov} onClick={onClose}>
      <div style={md} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isNew ? 'Yangi maqola' : 'Tahrirlash'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input className="input" placeholder="Sarlavha *" value={form.title} onChange={e => set('title', e.target.value)} />
          <input className="input" placeholder="Kategoriya (masalan: Skriptlar, FAQ)" value={form.category}
            list="kb-cats" onChange={e => set('category', e.target.value)} />
          <datalist id="kb-cats">
            {categories.map(c => <option key={c.category} value={c.category} />)}
          </datalist>
          <textarea className="input" placeholder="Matn (skript, ko'rsatma...)" rows={10}
            style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
            value={form.body} onChange={e => set('body', e.target.value)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 14 }}>Tartib:</label>
              <input className="input" type="number" style={{ width: 80 }} value={form.sort_order}
                onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
            </div>
            {!isNew && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                Faol (operatorlarga ko'rinsin)
              </label>
            )}
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 4 }}>
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)', padding: 16 };
const md = { background: 'var(--bg-secondary,#1e293b)', border: '1px solid var(--border,#2d3a4f)', borderRadius: 16, padding: 24, width: 560, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto' };
